import { LogoBorder } from 'assets/images'
import { FabricImage, Group, Line, Rect } from 'fabric'
import jsPDF from 'jspdf'
import { loadImage } from 'lib/image'
import axiosClient from 'services/axios-client'
import { colors } from 'style'
import WebFont from 'webfontloader'
import {
  BLEED,
  HALF_BLEED,
  HEIGHT_IN_PX_WITH_BLEED,
  MARGIN,
  SPINE_MARGIN,
  VISIBLE_HEIGHT,
  VISIBLE_WIDTH,
  WIDTH_WITH_BLEED_PX,
} from '../constants'

export const fonts = [
  'Myriad Pro Regular',
  'Delicious-Bold',
  'Verdana Italic',
  'Georgia Regular',
  'courier',
  'Comic Sans',
  'Maximum Impact Regular',
  'Monaco',
  'Optima',
  'Plaster Regular',
  'Engagement Regular',
  'Roboto-Bold',
  'EduNSWACTFoundation-Regular',
  'Anton-Regular',
  'Lobster-Regular',
  'DancingScript-Regular',
  'Pacifico-Regular',
  'Caveat-Regular',
  'IndieFlower-Regular',
  'Fondamento-Regular',
  'Andika-Regular',
  'Allan-Regular',
  'PollerOne-Regular',
  'UnifrakturMaguntia-Regular',
]

export const loadFonts = () =>
  WebFont.load({
    custom: {
      families: fonts,
    },
  })

export const loadBackgroundImage = async (canvas, url) => {
  const img = await FabricImage.fromURL(url)
  img.scaleToWidth(canvas.getWidth())
  img.scaleToHeight(canvas.getHeight())

  return img
}

export const zoomCanvas = (canvas, factor, coords = null) => {
  let zoom = canvas.getZoom()

  const zoomStep = 0.05
  zoom += zoomStep * factor
  zoom = Math.max(1, Math.min(zoom, 2)) // clamp zoom

  if (coords) {
    canvas.zoomToPoint(coords, zoom)
  } else {
    canvas.setZoom(zoom)
  }

  // It's fiddly to get the canvas precisely back into the visible area, so reset at max zoom.
  if (zoom === 1) {
    canvas.absolutePan({ x: 0, y: 0 })
  }

  canvas.requestRenderAll()
}

export const createPdf = async (
  backImage,
  spineImage,
  frontImage,
  spineWidth,
) => {
  const totalCoverWidth = WIDTH_WITH_BLEED_PX * 2 + spineWidth

  const pdf = new jsPDF({
    orientation: 'l',
    unit: 'pt',
    format: [totalCoverWidth * 0.24, HEIGHT_IN_PX_WITH_BLEED * 0.24],
  })

  pdf.addImage(
    backImage,
    'PNG',
    0,
    0,
    (WIDTH_WITH_BLEED_PX * 72) / 300,
    (HEIGHT_IN_PX_WITH_BLEED * 72) / 300,
  )
  pdf.addImage(
    spineImage,
    'PNG',
    (WIDTH_WITH_BLEED_PX * 72) / 300,
    0,
    (spineWidth * 72) / 300,
    (HEIGHT_IN_PX_WITH_BLEED * 72) / 300,
  )
  pdf.addImage(
    frontImage,
    'PNG',
    ((WIDTH_WITH_BLEED_PX + spineWidth) * 72) / 300,
    0,
    (WIDTH_WITH_BLEED_PX * 72) / 300,
    (HEIGHT_IN_PX_WITH_BLEED * 72) / 300,
  )

  return pdf
}

export async function generateThumbnail(orderedImageUrls) {
  const thumbnailHeight = 300

  const images = await Promise.all(orderedImageUrls.map(loadImage))

  const totalWidth = images.reduce((sum, img) => sum + img.width, 0)

  const tempCanvas = document.createElement('canvas')
  tempCanvas.width = totalWidth
  tempCanvas.height = VISIBLE_HEIGHT
  const tempCtx = tempCanvas.getContext('2d')

  let x = 0
  for (const img of images) {
    tempCtx.drawImage(img, x, 0)
    x += img.width
  }

  const aspect = totalWidth / VISIBLE_HEIGHT
  const finalWidth = Math.round(thumbnailHeight * aspect)

  const finalCanvas = document.createElement('canvas')
  finalCanvas.width = finalWidth
  finalCanvas.height = thumbnailHeight
  const finalCtx = finalCanvas.getContext('2d')

  finalCtx.drawImage(tempCanvas, 0, 0, finalWidth, thumbnailHeight)

  return await new Promise((resolve, reject) => {
    finalCanvas.toBlob((blob) => {
      if (blob) {
        resolve(blob)
      } else {
        reject(new Error('Failed to generate thumbnail'))
      }
    })
  })
}

export const uploadImageToBucket = async (img, bookId) => {
  const fileExtension = img.name.includes('.')
    ? img.name.split('.').pop()
    : 'png' // get the file extension

  const uniqueId = Math.random().toString(16).slice(2, 10)
  // Prevent clashes.
  const renamedImg = new File([img], uniqueId + '-' + img.name, {
    type: img.type,
  })

  const uploadRes = await axiosClient.post(
    `/book/${bookId}/cover/image/${renamedImg.name}`,
    {
      fileExtension,
    },
  )
  if (!uploadRes.ok) {
    throw new Error('Failed to create upload link')
  }

  const { url } = uploadRes.data

  await fetch(url, { method: 'PUT', body: renamedImg })

  const downloadRes = await axiosClient.get(
    `book/${bookId}/cover/image/${renamedImg.name}`,
  )
  if (!downloadRes.ok) {
    throw new Error('Failed to create download link')
  }

  return downloadRes.data
}

export const uploadThumbnailToBucket = async (img, bookId, coverName) => {
  const renamedImg = new File(
    [img],
    coverName + '-' + new Date().toISOString(),
    {
      type: img.type,
    },
  )

  const uploadRes = await axiosClient.post(
    `book/${bookId}/cover/image/${renamedImg.name}`,
  )
  if (!uploadRes.ok) {
    throw new Error('Failed to create upload link')
  }

  const { url } = uploadRes.data

  await fetch(url, { method: 'PUT', body: renamedImg })

  const downloadRes = await axiosClient.get(
    `book/${bookId}/cover/image/${renamedImg.name}`,
  )
  if (!downloadRes.ok) {
    throw new Error('Failed to create download link')
  }

  return renamedImg.name
}

export async function initializeSpineBackground(
  canvas,
  spineWidth,
  backgroundImage = null,
) {
  const marginRectangle = new Rect({
    width: spineWidth - SPINE_MARGIN,
    height: HEIGHT_IN_PX_WITH_BLEED - SPINE_MARGIN - BLEED * 2,
    top: BLEED,
    fill: '',
    stroke: colors.gray,
    strokeWidth: SPINE_MARGIN,
    draggable: false,
    hasControls: false,
    opacity: 0.6,
  })

  const bleedOptions = {
    stroke: colors.blueDark4,
    strokeWidth: BLEED,
    draggable: false,
    hasControls: false,
    opacity: 0.4,
  }

  const topBleed = new Line(
    [0, HALF_BLEED, spineWidth, HALF_BLEED],
    bleedOptions,
  )
  const bottomBleed = new Line(
    [
      0,
      HEIGHT_IN_PX_WITH_BLEED - HALF_BLEED,
      spineWidth,
      HEIGHT_IN_PX_WITH_BLEED - HALF_BLEED,
    ],
    bleedOptions,
  )

  const bgGroup = [marginRectangle, topBleed, bottomBleed]

  if (backgroundImage) {
    bgGroup.unshift(backgroundImage)
  }

  canvas.backgroundColor = 'white'
  canvas.backgroundImage = new Group(bgGroup)

  const logo = await FabricImage.fromURL(LogoBorder)
  logo.set({
    left: SPINE_MARGIN * 2,
    top: HEIGHT_IN_PX_WITH_BLEED * 0.833 + BLEED,
    draggable: false,
    hasControls: false,
  })
  logo.scaleToWidth(spineWidth - SPINE_MARGIN * 4)

  canvas.overlayImage = logo
}

export function initializeFrontOrBackCover(canvas, isFront, bgImage = null) {
  const marginOptions = {
    height: VISIBLE_HEIGHT - MARGIN, // Half of stroke is outside the height
    width: VISIBLE_WIDTH - MARGIN,
    top: BLEED,
    left: 0,
    fill: '',
    stroke: colors.gray,
    strokeWidth: MARGIN,
    draggable: false,
    hasControls: false,
    opacity: 0.6,
  }

  const bleedOptions = {
    stroke: colors.blueDark4,
    strokeWidth: BLEED,
    draggable: false,
    hasControls: false,
    opacity: 0.4,
  }

  let marginRectangle
  let bleedTop
  let bleedSide
  let bleedBottom

  // Calculations take into acount the fact that lines are measured from their center, not absolutely from the top corner.
  if (isFront) {
    bleedTop = new Line(
      [0, HALF_BLEED, WIDTH_WITH_BLEED_PX, HALF_BLEED],
      bleedOptions,
    )
    bleedSide = new Line(
      [
        VISIBLE_WIDTH + HALF_BLEED,
        BLEED,
        VISIBLE_WIDTH + HALF_BLEED,
        VISIBLE_HEIGHT + BLEED,
      ],
      bleedOptions,
    )
    bleedBottom = new Line(
      [
        0,
        HEIGHT_IN_PX_WITH_BLEED - HALF_BLEED,
        WIDTH_WITH_BLEED_PX,
        HEIGHT_IN_PX_WITH_BLEED - HALF_BLEED,
      ],
      bleedOptions,
    )
    marginRectangle = new Rect(marginOptions)
  } else {
    bleedTop = new Line(
      [0, HALF_BLEED, WIDTH_WITH_BLEED_PX, HALF_BLEED],
      bleedOptions,
    )

    bleedSide = new Line(
      [HALF_BLEED, BLEED, HALF_BLEED, VISIBLE_HEIGHT + BLEED],
      bleedOptions,
    )
    bleedBottom = new Line(
      [
        0,
        HEIGHT_IN_PX_WITH_BLEED - HALF_BLEED,
        WIDTH_WITH_BLEED_PX,
        HEIGHT_IN_PX_WITH_BLEED - HALF_BLEED,
      ],
      bleedOptions,
    )
    marginRectangle = new Rect({
      ...marginOptions,
      left: BLEED,
    })
  }

  let groupMembers = [marginRectangle, bleedTop, bleedSide, bleedBottom]
  if (bgImage) {
    groupMembers.unshift(bgImage)
  }

  const group = new Group(groupMembers)
  group.selectable = false
  group.evented = false
  canvas.backgroundColor = 'white'
  canvas.backgroundImage = group
  canvas.renderAll()
}

export function resetCanvasHistory(dispatchHistory, key, front, spine, back) {
  const initialFront = front.toJSON()
  const initialSpine = spine.toJSON()
  const initialBack = back.toJSON()

  dispatchHistory({
    type: 'RESET',
    initialState: {
      key: key || '_UNSAVED',
      canvases: {
        front: { stack: [initialFront], index: 0 },
        spine: { stack: [initialSpine], index: 0 },
        back: { stack: [initialBack], index: 0 },
      },
    },
  })
}
